import { Link } from 'react-router';
import type { MinistryDetail } from '../../shared/api/types';
import { useMe } from '../session/SessionProvider';
import { useVisitorAccess } from '../visitors/shared';
import { useVerifiedDenomination } from '../denomination/denomination';
import { Icon } from '../shell/nav';

interface Tool {
  to: string;
  label: string;
  hint: string;
  icon: string;
  visible: boolean;
}

/**
 * Las herramientas de cada ministerio, a la mano desde su ficha (Alex, 2026-11-02: el líder no tiene
 * que ir a Herramientas a buscarlas). Cada botón aparece solo si la persona tiene el permiso; el menú
 * Herramientas sigue igual.
 */
export function useMinistryTools(m: MinistryDetail): Tool[] {
  const me = useMe();
  const visitors = useVisitorAccess(me.activeChurchId);
  const ad = useVerifiedDenomination(me.activeChurchId) === 'asambleas-de-dios';
  const presentations: Tool = { to: '/app/presentaciones', label: 'Proyectar', hint: 'La consola para proyectar cantos, textos e imágenes.', icon: 'screen', visible: me.can('presentation.read') };
  const visits: Tool = { to: '/app/miembros?tab=visitas', label: 'Visitas', hint: 'Quiénes nos visitaron y su seguimiento.', icon: 'wave', visible: visitors.canUse };
  const trivia: Tool = { to: '/app/biblia/trivia', label: 'Trivia bíblica', hint: 'Proyecta preguntas y cada quien contesta desde su celular.', icon: 'check', visible: true };
  // Para quien dirige el ministerio (líder o co-líder): encuesta, recaudación y chat del ministerio.
  const leads = m.leader?.personId === me.personId || m.coLeaders.some((c) => c.personId === me.personId);
  const forLeader: Tool[] = [
    { to: `/app/encuestas?ministerio=${m.id}`, label: 'Encuesta al ministerio', hint: `Pregunta algo a los integrantes de ${m.name}.`, icon: 'check', visible: leads },
    { to: `/app/recaudaciones?ministerio=${m.id}`, label: 'Recaudación', hint: 'Una venta para juntar fondos para el ministerio.', icon: 'coin', visible: leads },
    { to: `/app/chats/${m.id}`, label: 'Chat del ministerio', hint: 'Conversa con todo el equipo.', icon: 'chat', visible: leads },
  ];
  const tools: Record<string, Tool[]> = {
    multimedia: [
      { to: '/app/multimedia', label: 'Multimedia', hint: 'Fotos, videos y archivos para proyectar.', icon: 'image', visible: me.can('media.read') },
      presentations,
    ],
    alabanza: [
      { to: '/app/cantos', label: 'Cantos', hint: 'Letra, tono y BPM de cada canto.', icon: 'music', visible: me.can('worship.manage') || me.can('presentation.read') },
      { to: '/app/ensayos', label: 'Ensayos', hint: 'Cuándo se ensaya y qué cantos.', icon: 'calendar', visible: me.can('worship.read') },
      presentations,
    ],
    finanzas: [{ to: '/app/finanzas', label: 'Finanzas', hint: 'Cuentas, ingresos y gastos, ofrendas del culto, metas y cierres.', icon: 'coin', visible: me.can('finance.read') }],
    guarderia: [{ to: '/app/guarderia', label: 'Guardería', hint: 'Recibir y entregar a los niños y anotar lo de cada sala.', icon: 'baby', visible: me.can('nursery.operate') }],
    oracion: [{ to: '/app/oracion', label: 'Oración', hint: 'Peticiones, jornadas de oración y cómo exportarlas.', icon: 'pray', visible: me.can('prayer.read') }],
    mantenimiento: [{ to: '/app/mantenimiento', label: 'Mantenimiento', hint: 'Reportes y arreglos del templo.', icon: 'wrench', visible: me.can('church.read') }],
    bienvenida: [visits],
    diaconado: [visits, { to: '/app/ordenanzas', label: 'Bautismos y presentaciones', hint: 'Inscripciones para bautizarse y presentar niños.', icon: 'drop', visible: me.can('church.read') }],
    cafeteria: [
      { to: '/app/comercio', label: 'Cafetería y tienda', hint: 'Vender en la cafetería y la tienda.', icon: 'cup', visible: me.can('commerce.sell') },
      { to: '/app/cocina', label: 'Pantalla de cocina', hint: 'Los pedidos en columnas para la cocina.', icon: 'screen', visible: me.can('commerce.sell') },
    ],
    campamento: [{ to: '/app/campamentos', label: 'Campamentos', hint: 'Campamentos y retiros: inscripciones y organización.', icon: 'flag', visible: me.can('church.read') }],
    eventos_especiales: [{ to: '/app/eventos', label: 'Eventos', hint: 'Aniversarios, celebraciones y su registro.', icon: 'calendar', visible: me.can('calendar.read') }],
    servicio_social: [{ to: '/app/ayuda', label: 'Ayuda comunitaria', hint: 'Lo que piden y ofrecen los hermanos.', icon: 'hand', visible: me.can('church.read') }],
    ninos: [{ to: '/app/asambleas/ebv', label: 'Escuela Bíblica de Vacaciones', hint: 'Inscripción, grupos y lista diaria.', icon: 'sprout', visible: ad && me.can('departments.read') }, trivia],
    jovenes: [trivia],
    ad_escuela_dominical: [{ to: '/app/asambleas/escuela-dominical', label: 'Escuela Dominical', hint: 'Clases por edad y el registro de cada domingo.', icon: 'book', visible: me.can('departments.read') }],
    ad_exploradores: [{ to: '/app/asambleas/exploradores', label: 'Exploradores del Rey', hint: 'Ramas, insignias, rangos y avance de cada niño.', icon: 'flag', visible: me.can('departments.read') }],
    ad_misioneritas: [{ to: '/app/asambleas/misioneritas', label: 'Misioneritas', hint: 'Grupos, insignias, rangos y avance de cada niña.', icon: 'heart', visible: me.can('departments.read') }],
    ad_misiones: [
      { to: '/app/asambleas/ofrendas', label: 'Ofrendas misioneras', hint: 'Recibido, enviado y por enviar.', icon: 'globe', visible: me.can('finance.read') },
      { to: '/app/misiones', label: 'Misioneros', hint: 'Misioneros y proyectos que apoya la iglesia.', icon: 'globe', visible: me.can('church.read') },
    ],
  };
  return [...(m.systemKey ? (tools[m.systemKey] ?? []) : []), ...forLeader].filter((t) => t.visible);
}

/** «Herramientas del ministerio»: accesos directos arriba de la ficha. */
export function MinistryTools({ ministry }: { ministry: MinistryDetail }) {
  const tools = useMinistryTools(ministry);
  if (tools.length === 0) return null;
  return (
    <section className="ministry-tools" aria-labelledby="ministry-tools-title">
      <h2 id="ministry-tools-title">Herramientas del ministerio</h2>
      <ul className="hub">
        {tools.map((t) => (
          <li key={t.to}>
            <Link to={t.to} className="hub__item">
              <span className="hub__icon">
                <Icon name={t.icon} />
              </span>
              <span>
                <strong>{t.label}</strong>
                <span className="small muted">{t.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
